/* KANON `Cwiczenia` 17.09.2026 — the filter panel's four BODIES.
 *
 * The shell around them is `shared/ui/filterPanel.module.css`: the popup frame, the head, the tab
 * strip, the body box and the footer, transcribed from the Programy panel this specimen copies
 * verbatim. Nothing here restates a shell property — a declaration on both sides is settled by
 * chunk order, which differs between the dev server and the build.
 *
 * What is this screen's own is the four tabs, and they are not the plan library's four:
 *   Typ       chips carrying the TYPE's glyph and hue (--type-ink / --type-wash)
 *   Mięśnie   a checklist in four regions, each row with a box, a word and its count
 *   Sprzęt    a grid of photographs, six across, with the piece's own picture
 *   Źródło    three radio rows, and they write the SCOPE rather than a facet of their own
 *
 * `.control` — the off-screen native input every row wraps — is declared HERE rather than in the
 * shared shell, because a `:has(.control:focus-visible)` selector compiled in this file looks for
 * THIS file's hashed class name. The shared module says the same thing in its own header.
 *
 * Every text leaf states its line-height. Heights and widths are layout rather than design tokens
 * (see stylelint.config.js); every colour, space, radius and type value below is a token. */

/* This screen's panel carries SIX tabs where the plan library's carries four, and at the shell's
 * 342.5px the sixth („Język") was clipped: the strip's scrollWidth 348 against 341. So the popup is
 * wider here, and only here. The shell reads its width from `--w-pl-panel`, so this restates the
 * VALUE that hook carries rather than the `width` property — a second `width` on the same element
 * would be settled by chunk order. */
.panel {
  --w-pl-panel: 392px;
}

/* Every checkbox and radio is off screen rather than `display: none`, which would take it out of
 * the accessibility tree and with it the whole keyboard contract. The label around it is the control
 * a trainer sees, so the ring is drawn there.
 *
 * Each label is `position: relative` so the input is positioned against ITS OWN control rather than
 * against whatever positioned ancestor happens to be up the tree — inside a portalled popup that is
 * the positioner, and the input lands somewhere the pointer cannot reach it. Measured on the plan
 * library: Playwright refused a `check()` with "element is outside of the viewport". */
.control {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

/* ── Typ ───────────────────────────────────────────────────────────────────────────────────── */

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6-5px);
}

/* A type chip. Neutral until it is chosen, and then it carries the type's own hue — the same
 * treatment the plan library gives a category, on this screen's own colour vocabulary. */
.chip {
  position: relative;
  display: inline-flex;
  gap: var(--space-6-5px);
  align-items: center;
  height: var(--h-pl-tab-chip);
  padding: var(--space-0) var(--space-10-5px);
  font-size: var(--fs-pl-control);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-mark);
  cursor: pointer;
}

.chipOn {
  color: var(--type-ink);
  background: var(--type-wash);
  border-color: var(--type-ink);
}

/* The glyph is inked by the TYPE at all times — on a neutral chip it is the only thing carrying the
 * hue, which is owner decision D9's rule for the creator's tiles and reads the same here. */
.chipGlyph {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--type-ink);
}

.chipCheck {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--type-ink);
}

.chip:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Mięśnie ───────────────────────────────────────────────────────────────────────────────── */

/* Fourteen groups in four regions. The list scrolls inside the panel rather than growing it — the
 * panel hangs off the card's top-left corner and a taller one would reach past the viewport. */
.regions {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  max-height: var(--h-pl-client-list);
  overflow-y: auto;
}

.regionTitle {
  display: block;
  margin-bottom: var(--space-1);
  font-size: var(--fs-pl-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-eyebrow);
  text-transform: uppercase;
  color: var(--text-3);
}

.check {
  position: relative;
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  width: 100%;
  height: 28px;
  font-size: var(--fs-pl-option);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  text-align: left;
  color: var(--text);
  cursor: pointer;
}

.box {
  display: grid;
  place-items: center;
  flex: none;
  width: 16px;
  height: 16px;
  color: transparent;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-5px);
}

.checkOn .box {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.boxGlyph {
  display: block;
  width: 11px;
  height: 11px;
}

.checkCount {
  margin-left: auto;
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.check:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Sprzęt ────────────────────────────────────────────────────────────────────────────────── */

.equipment {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-9px) var(--space-8);
  max-height: var(--h-pl-client-list);
  overflow-y: auto;
}

.equipCell {
  position: relative;
  cursor: pointer;
}

/* The piece's own photograph. `photoBox` supplies the fit and the ground; the square and the radius
 * are this surface's. A chosen tile takes a DOUBLED ink edge rather than a tint: a photograph cannot
 * invert, and hiding the thing the tile exists to show is not a state. The specimen draws a 1px
 * border plus a 1px inset ring, which is the same 2px of ink — written as a border here because
 * stylelint accepts only a token in `box-shadow`, and an inset ring is geometry rather than
 * elevation. `box-sizing: border-box` keeps the square the same size, so nothing moves. */
.equipImage {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.equipCellOn .equipImage {
  border-width: var(--border-thick);
  border-color: var(--text);
}

/* A piece the catalogue names no picture for. Its initial, on the same square. */
.equipLetter {
  display: grid;
  place-items: center;
  width: 100%;
  aspect-ratio: 1;
  font-size: var(--fs-pl-control);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.equipLabel {
  display: -webkit-box;
  margin-top: var(--space-4px);
  overflow: hidden;
  font-size: var(--fs-pl-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-display);
  text-align: center;
  color: var(--text-2);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.equipCell:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Źródło ────────────────────────────────────────────────────────────────────────────────── */

/* Źródło is a RADIO GROUP, so it is a `<fieldset>` named by its own visible title — the same shape
 * `ExerciseScopeSwitch` uses for the three words above the table. Three `name`d radios with no
 * group around them are three unrelated controls to a screen reader, which announces neither how
 * many there are nor what they are choosing between.
 *
 * The box is reset to nothing and laid out as the tab body already laid its two children out, so
 * the drawing is unchanged. `float` is what puts the legend back in the flow: a `<legend>` is the
 * fieldset's "rendered legend" and therefore NOT a flex item, and floating it hands it back as an
 * ordinary one — the float itself is then ignored, as it is on every flex item. The accessible name
 * comes from the first `<legend>` either way. */
.sourceGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  min-inline-size: var(--space-0);
  margin: var(--space-0);
  padding: var(--space-0);
  border-width: var(--space-0);
}

.sourceLegend {
  float: left;
  width: 100%;
  padding: var(--space-0);
}

.sources {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.source {
  position: relative;
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  width: 100%;
  padding: var(--space-5-5px) var(--space-7px);
  font-size: var(--fs-pl-option);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  text-align: left;
  color: var(--text);
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
  cursor: pointer;
}

.sourceOn {
  background: var(--bg-inset);
  border-color: var(--text);
}

.dot {
  display: grid;
  place-items: center;
  flex: none;
  width: 16px;
  height: 16px;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.sourceOn .dot {
  border-color: var(--text);
}

.dot::after {
  width: 7px;
  height: 7px;
  content: '';
  background: transparent;
  border-radius: var(--r-pill);
}

.sourceOn .dot::after {
  background: var(--text);
}

.sourceCount {
  margin-left: auto;
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.source:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── Tagi ──────────────────────────────────────────────────────────────────────────────────── */

/* The rows scroll inside the body: a trainer may hold two hundred tags, and the panel's height is
 * the body's, so an unbounded list would push the foot off a short window. The bound is the
 * equipment grid's own, so the panel stands the same height whichever of the two is open. */
.tagRows {
  --sb-size: 6px;

  display: flex;
  flex-direction: column;
  max-height: var(--h-pl-client-list);
  padding-right: var(--space-1);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.tagLabel {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tagNote {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text-2);
}

/* „Zarządzaj tagami" opens something rather than undoing, so it is the foot's ink word — the
 * canon's own treatment. The doubled class outranks the shell's `.clear`, which sets the same property
 * at the same specificity from another module — chunk order differs between dev and build. */
.manageTags.manageTags {
  color: var(--text);
}
